import LayoutContainer from 'components/layout/LayoutContainer';
import React, { useCallback, useEffect, useState } from 'react';
import Card from 'components/inputs/Card';
import { FETCH_PAYMENT_STATUS } from 'actions/customer';
import { useDispatch, useSelector } from 'react-redux';
import * as _ from 'lodash';
import { useParams } from 'react-router-dom';
import ReactPagination from 'components/general/ReactPagination';
import { BiSort } from 'react-icons/bi';
import { roundOfDigit } from 'components/common/utils';
import moment from 'moment';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import { useTranslation } from 'react-i18next';
import Select from 'components/inputs/Select';

const PaymentStatus = () => {
  const dispatch = useDispatch();
  const { customerId } = useParams();
  const [sortByItem, setSortByItem] = useState({ item: '', order: '' });
  const [daysFilter, setDaysFilter] = useState(7); // Default to last 7 days
  const { t } = useTranslation();

  const paymentStatusList = useSelector((state) => state.paymentStatus?.paymentStatus || []);
  const page = useSelector((state) => state.paymentStatus?.page || 1);
  const totalData = useSelector((state) => state.paymentStatus?.total || 0);
  const limit = useSelector((state) => state.paymentStatus?.limit || 10);
  const totalPages = useSelector((state) => state.paymentStatus?.totalPages || 1);
  const isLoader = useSelector((state) => state.paymentStatus?.isLoading || false);

  const getPaymentStatus = useCallback(
    (data) => {
      const paymentData = {
        ...data,
        id: customerId,
        days: daysFilter,
      };
      dispatch({ type: FETCH_PAYMENT_STATUS.REQUEST, payload: paymentData });
    },
    [customerId, daysFilter]
  );

  const getSortByItem = useCallback(
    (name, order, page) => {
      const sortedOrder = order || 'asc';
      if (page) {
        const data = {
          sortBy: `${sortedOrder === 'asc' ? '' : '-'}${name}`,
          page: page.selected + 1,
        };
        getPaymentStatus(data);
      } else {
        const data = {
          sortBy: `${sortedOrder === 'asc' ? '' : '-'}${name}`,
        };
        getPaymentStatus(data);
      }
    },
    [getPaymentStatus]
  );

  const handlePageClick = useCallback(
    (page) => {
      if (sortByItem.item && sortByItem.order) {
        getSortByItem(sortByItem.item, sortByItem.order, page);
      } else {
        const data = {
          page: page.selected + 1,
        };
        getPaymentStatus(data);
      }
    },
    [sortByItem, getPaymentStatus, getSortByItem]
  );

  useEffect(() => {
    getPaymentStatus();
  }, [getPaymentStatus]);

  const handleSorting = (sortItem, dataItem) => {
    if (dataItem.item === sortItem) {
      const sortOrder = dataItem.order === 'asc' ? 'desc' : 'asc';
      setSortByItem({ item: sortItem, order: sortOrder });
      getSortByItem(sortItem, sortOrder);
    } else {
      setSortByItem({ item: sortItem, order: 'asc' });
      getSortByItem(sortItem, 'asc');
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      success: '#0f9b72',
      completed: '#0f9b72',
      pending: '#ff9800',
      failed: '#be210b',
      refunded: '#2196f3',
    };
    const color = statusColors[status?.toLowerCase()] || '#6c757d';
    return (
      <span style={{
        padding: '4px 12px',
        backgroundColor: color,
        color: '#fff',
        borderRadius: '4px',
        fontSize: '12px',
        fontWeight: '500',
        textTransform: 'capitalize'
      }}>
        {status || '-'}
      </span>
    );
  };

  const daysFilterOptions = [
    { label: 'Last 7 Days', value: 7 },
    { label: 'Last 15 Days', value: 15 },
    { label: 'Last 30 Days', value: 30 },
    { label: 'Last 45 Days', value: 45 },
    // { label: 'Last 90 Days', value: 90 },
  ];

  return (
    <React.Fragment>
      <LayoutContainer>
        <SkeletonTheme baseColor="#1b2a38" highlightColor="#15212b" enableAnimation="true">
          <Card className="customerDetail-table">
            <div className="customerDetail-table-heading">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                <span>
                  {isLoader ? <Skeleton height={15} width={100} /> : 'Payment Status'}
                </span>
                <div style={{ minWidth: '200px' }}>
                  <Select
                    placeholder="Select Days"
                    value={daysFilter}
                    options={daysFilterOptions}
                    onChange={(val) => setDaysFilter(val)}
                  />
                </div>
              </div>
            </div>
            <div className="customerDetail-list-table">
              <div className="table-responsive">
                <table className="record-list-table">
                  <thead>
                    <tr>
                      <th>
                        <div className="sorting">
                          <span>{isLoader ? <Skeleton height={15} width={100} /> : t('customerDetail.srNo')}</span>
                        </div>
                      </th>
                      <th>
                        <div className="sorting">
                          <span>{isLoader ? <Skeleton height={15} width={100} /> : 'Payment ID'}</span>
                          <span className="ico" onClick={() => handleSorting('payment_id', sortByItem)}>
                            <BiSort size={15} />
                          </span>
                        </div>
                      </th>
                      <th>
                        <div className="sorting">
                          <span>{isLoader ? <Skeleton height={15} width={100} /> : 'Amount'}</span>
                          <span className="ico" onClick={() => handleSorting('amount', sortByItem)}>
                            <BiSort size={15} />
                          </span>
                        </div>
                      </th>
                      <th>
                        <div className="sorting">
                          <span>{isLoader ? <Skeleton height={15} width={100} /> : 'Status'}</span>
                          <span className="ico" onClick={() => handleSorting('status', sortByItem)}>
                            <BiSort size={15} />
                          </span>
                        </div>
                      </th>
                      <th>
                        <div className="sorting">
                          <span>{isLoader ? <Skeleton height={15} width={100} /> : 'Payment Type'}</span>
                          <span className="ico" onClick={() => handleSorting('payment_type', sortByItem)}>
                            <BiSort size={15} />
                          </span>
                        </div>
                      </th>
                      <th>
                        <div className="sorting">
                          <span>{isLoader ? <Skeleton height={15} width={100} /> : 'Date & Time'}</span>
                          <span className="ico" onClick={() => handleSorting('createdAt', sortByItem)}>
                            <BiSort size={15} />
                          </span>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {_.isEmpty(paymentStatusList) ? (
                      <tr>
                        <td colSpan={6} className="border-0">
                          <div className="empty-data-block">
                            {isLoader ? 'Loading...' : 'No payment records found'}
                          </div>
                        </td>
                      </tr>
                    ) : (
                      _.map(paymentStatusList, (item, key) => {
                        const serial_num = limit * (page - 1) + key;
                        return (
                          <tr key={`payment-status-data-item-${serial_num}`}>
                            <td>{isLoader ? <Skeleton height={15} width={100} /> : serial_num + 1}</td>
                            <td>{isLoader ? <Skeleton height={15} width={100} /> : _.get(item, 'payment_id', '-')}</td>
                            <td>
                              {isLoader ? (
                                <Skeleton height={15} width={100} />
                              ) : (
                                `₹ ${roundOfDigit(_.get(item, 'amount', 0), 2)}`
                              )}
                            </td>
                            <td>
                              {isLoader ? (
                                <Skeleton height={15} width={100} />
                              ) : (
                                getStatusBadge(_.get(item, 'status', '-'))
                              )}
                            </td>
                            <td>
                              {isLoader ? (
                                <Skeleton height={15} width={100} />
                              ) : (
                                <span style={{ textTransform: 'capitalize' }}>
                                  {_.get(item, 'payment_type', '-')}
                                </span>
                              )}
                            </td>
                            <td>
                              {isLoader ? (
                                <Skeleton height={15} width={100} />
                              ) : item.createdAt ? (
                                moment(item.createdAt).format('DD/MM/YYYY HH:mm:ss')
                              ) : (
                                '-'
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            {!_.isEmpty(paymentStatusList) && (
              <ReactPagination
                currentPage={page}
                limit={limit}
                total={totalData}
                handlePageClick={(pageVal) => handlePageClick(pageVal)}
                totalPages={totalPages}
                marginPagesDisplayed={1}
              />
            )}
          </Card>
        </SkeletonTheme>
      </LayoutContainer>
    </React.Fragment>
  );
};

export default PaymentStatus;
